/**
 * @file ページの書き込みを解き直す (過去の回を残す・見る・消す) hook である。
 *
 * @what: 「もう一度解く」で、いまの線を過去の回 (n 回目) として Storage に置いてから、いまの線を
 *   空にする。過去の回は「ページに書き込む」の間だけ切り替えて見られ (読むだけ)、消せる。
 *   回の番号は残っている中で一番大きい番号の次にする (途中を消しても番号は詰めない)。
 *   過去の回を置いた日は、ファイルの日時から引く (切り替えの選択肢に出す)。
 * @why: 同じ紙面の上で前回の解答と見比べたい。線のファイルを回ごとに分けて置けば、いまの線の
 *   読み書きの経路 (usePageInk) を変えずに済む。
 *   残す方が先で、成功してから空にする。※なぜなら空にしてから残すと、残せなかったときに線だけが
 *   消えるためである。
 * @failure: 残す・消すの失敗は文言にして返し、状態は変えない。過去の回の読みの失敗は空として出す。
 */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  PAGE_INK_ATTEMPTS_MAX,
  deletePageInk,
  loadPageInk,
  loadPageInkSavedAt,
  pageInkAttemptPath,
  savePageInk,
} from "../lib/pageInk";
import type { HandwritingDoc } from "../model/handwriting";

export function usePageInkAttempts({
  familyId,
  learnerId,
  sourceId,
  page,
  active,
  attempts,
  currentDoc,
  clearCurrent,
  addAttempt,
  removeAttempt,
}: {
  familyId: string;
  learnerId: string | null;
  sourceId: string;
  page: number | null;
  /** 「ページに書き込む」が入っているか (過去の回の日付は、このときだけ引く)。 */
  active: boolean;
  /** このページに残してある過去の回の番号 (昇順)。 */
  attempts: readonly number[];
  /** いまの線である。 */
  currentDoc: HandwritingDoc;
  /** いまの線を空にして待たずに保存する (usePageInk.clearNow)。 */
  clearCurrent: () => Promise<void>;
  addAttempt: (page: number, attempt: number) => void;
  removeAttempt: (page: number, attempt: number) => void;
}) {
  /** いま見ている回である。null は「いま」(書ける)。 */
  const [viewing, setViewing] = useState<number | null>(null);
  const [docs, setDocs] = useState<ReadonlyMap<number, HandwritingDoc | null>>(() => new Map());
  const [savedAt, setSavedAt] = useState<ReadonlyMap<number, Date | null>>(() => new Map());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const docsRef = useRef(docs);
  docsRef.current = docs;

  const key = learnerId !== null && page !== null ? `${familyId}/${learnerId}/${sourceId}/${page}` : null;
  const keyRef = useRef(key);
  keyRef.current = key;
  const pathOf = useCallback(
    (attempt: number) =>
      learnerId !== null && page !== null
        ? pageInkAttemptPath(familyId, learnerId, sourceId, page, attempt)
        : null,
    [familyId, learnerId, sourceId, page],
  );

  // 日付を引き始めた回である (同じ回を二度引かないため)。ページが変わったら空にする。
  const askedRef = useRef<Set<number>>(new Set());

  // ページが変わったら「いま」に戻り、読んだ回の線と日付は捨てる。
  useEffect(() => {
    setViewing(null);
    setDocs(new Map());
    setSavedAt(new Map());
    setError(null);
    askedRef.current = new Set();
  }, [key]);

  // 書き込みの間だけ、まだ引いていない回の日付を引く。
  useEffect(() => {
    if (!active) return;
    const pageKey = key;
    for (const n of attempts) {
      if (askedRef.current.has(n)) continue;
      const p = pathOf(n);
      if (p === null) continue;
      askedRef.current.add(n);
      loadPageInkSavedAt(p)
        .catch((err: unknown) => {
          console.error("[study] failed to read page ink attempt time", err);
          return null;
        })
        .then((d) => {
          // ページが変わっていたら捨てる (前のページの日付を新しいページに載せない)。
          if (pageKey !== keyRef.current) return;
          setSavedAt((prev) => (prev.has(n) ? prev : new Map(prev).set(n, d)));
        });
    }
  }, [active, attempts, key, pathOf]);

  /** 見る回を切り替える。過去の回は初めて見るときに読む。 */
  const view = useCallback(
    (attempt: number | null) => {
      setViewing(attempt);
      setError(null);
      if (attempt === null || docsRef.current.has(attempt)) return;
      const p = pathOf(attempt);
      if (p === null) return;
      const pageKey = keyRef.current;
      loadPageInk(p)
        .catch((err: unknown) => {
          console.error("[study] failed to load page ink attempt", err);
          return null;
        })
        .then((doc) => {
          if (pageKey !== keyRef.current) return;
          setDocs((prev) => (prev.has(attempt) ? prev : new Map(prev).set(attempt, doc)));
        });
    },
    [pathOf],
  );

  const nextAttempt = (attempts.length > 0 ? Math.max(...attempts) : 0) + 1;
  const canStartNew =
    !busy && viewing === null && currentDoc.strokes.length > 0 && attempts.length < PAGE_INK_ATTEMPTS_MAX;

  /** いまの線を次の回として残し、ページを空にする。 */
  const startNew = useCallback(async () => {
    if (busy || page === null || currentDoc.strokes.length === 0) return;
    if (attempts.length >= PAGE_INK_ATTEMPTS_MAX) return;
    const n = nextAttempt;
    const p = pathOf(n);
    if (p === null) return;
    setBusy(true);
    setError(null);
    try {
      await savePageInk(p, currentDoc);
    } catch (err) {
      console.error("[study] failed to save page ink attempt", err);
      setError("残せませんでした。もう一度お試しください。");
      setBusy(false);
      return;
    }
    addAttempt(page, n);
    setDocs((prev) => new Map(prev).set(n, currentDoc));
    setSavedAt((prev) => new Map(prev).set(n, new Date()));
    await clearCurrent();
    setBusy(false);
  }, [busy, page, currentDoc, attempts.length, nextAttempt, pathOf, addAttempt, clearCurrent]);

  /** 過去の回を消す。見ていた回なら「いま」に戻る。 */
  const remove = useCallback(
    async (attempt: number) => {
      if (busy || page === null) return;
      const p = pathOf(attempt);
      if (p === null) return;
      setBusy(true);
      setError(null);
      try {
        await deletePageInk(p);
      } catch (err) {
        console.error("[study] failed to delete page ink attempt", err);
        setError("消せませんでした。もう一度お試しください。");
        setBusy(false);
        return;
      }
      removeAttempt(page, attempt);
      setDocs((prev) => {
        const next = new Map(prev);
        next.delete(attempt);
        return next;
      });
      setSavedAt((prev) => {
        const next = new Map(prev);
        next.delete(attempt);
        return next;
      });
      setViewing((v) => (v === attempt ? null : v));
      setBusy(false);
    },
    [busy, page, pathOf, removeAttempt],
  );

  /** 見ている回の線である。「いま」なら null。読み終わるまでは undefined。 */
  const viewingDoc: HandwritingDoc | null | undefined =
    viewing === null ? null : docs.has(viewing) ? (docs.get(viewing) ?? null) : undefined;

  return {
    viewing,
    viewingDoc,
    savedAt,
    view,
    startNew,
    remove,
    busy,
    error,
    nextAttempt,
    canStartNew,
    limitReached: attempts.length >= PAGE_INK_ATTEMPTS_MAX,
  };
}
